import addDays from 'date-fns/addDays';
import formatISO from 'date-fns/formatISO';
import getUnixTime from 'date-fns/getUnixTime';
import { EstablishmentElasticSearchModel, EstablishmentViewModel } from "../establishment/establishment.model.v1";
import Yup from "../validation/yup";
import { SubjectValues } from "./offer.enum";
import { FORBIDDEN_CHARS_REGEX } from "../validation/regex";
import { LocationElasticSearchModel } from "../location/location.model.v1";
import { LocationViewModel } from "../location/location.model.v1";
import { SalaryUnit } from "../salary/salary";
const { v4: uuid } = require('uuid');
const VERSION = 4;
export class OfferFormModel {
    constructor() {
        this.employmentType = null;
        this.contractType = null;
        this.teachingType = null;
        this.salary = {
            currency: 'EURO',
            type: 'value',
            unit: SalaryUnit.HOUR,
            value: 0,
        };
        this.subject = SubjectValues.ENGLISH;
        this.title = '';
        this.jobTitles = [];
        this.city = '';
        this.location = null;
        this.levels = [];
        this.jobStartDate = null;
        this.jobEndDate = null;
        this.description = '';
        this.candidateRequirements = '';
        this.benefits = '';
        this.prefferedLocale = 'en';
    }
    static get validationSchema() {
        return Yup.object({
            title: Yup.string().matches(FORBIDDEN_CHARS_REGEX, () => ({ key: 'common:formValidation.forbiddenCharsFormat' })).min(10).max(60).trim().required(),
            location: Yup.mixed().required(),
            jobTitles: Yup.array().of(Yup.string().required()).min(1).required(),
            levels: Yup.array().of(Yup.string().required()).min(1).required(),
            employmentType: Yup.string().required(),
            contractType: Yup.string().required(),
            teachingType: Yup.string().required(),
            salary: Yup.object({
                unit: Yup.string().required(),
                value: Yup.number().required().when('unit', (unit, schema) => {
                    switch (unit) {
                        case SalaryUnit.HOUR:
                            return schema.min(10.25, () => ({ key: 'common:formValidation.minSalary' })).required();
                        case SalaryUnit.MONTH:
                            return schema.min(1539.42, () => ({ key: 'common:formValidation.minSalary' })).required();
                        case SalaryUnit.YEAR:
                            return schema.min(18654, () => ({ key: 'common:formValidation.minSalary' })).required();
                        default:
                            return schema.min(0).required();
                    }
                }),
                negotiable: Yup.boolean(),
            }),
            subject: Yup.string().required(),
            jobStartDate: Yup.mixed().required(),
            jobEndDate: Yup.mixed().nullable().default(null),
            description: Yup.string().max(10000).trim().required(),
            candidateRequirements: Yup.string().max(10000).trim().required(),
            benefits: Yup.string().max(5000)
        });
    }
}
OfferFormModel.VERSION = VERSION;
export class OfferDDBModel {
    constructor(slug, source, establishmentId, status, offerDurationDays) {
        this.slug = slug;
        this.subject = source.subject;
        this.type = source.type;
        this.employmentType = source.employmentType;
        this.contractType = source.contractType;
        this.teachingType = source.teachingType;
        this.salary = source.salary;
        this.title = source.title.replace(/[<|>|/]/g, '');
        this.jobTitles = source.jobTitles;
        this.city = source.city;
        this.location = source.location;
        this.levels = source.levels;
        this.jobStartDate = source.jobStartDate;
        this.jobEndDate = source.jobEndDate;
        this.description = source.description;
        this.candidateRequirements = source.candidateRequirements;
        this.benefits = source.benefits;
        this.prefferedLocale = source.prefferedLocale;
        this.establishmentId = establishmentId;
        this.status = status;
        this.submissionDate = formatISO(Date.now());
        this.publicationDate = formatISO(Date.now());
        // TTL
        this.refreshExpirationDate(offerDurationDays);
        this.expirationReminderSent = 'no';
        this.emailEnabled = true;
        this.emailUnsubscribeToken = uuid();
    }
    refreshExpirationDate(offerDurationDays) {
        this.publicationDate = formatISO(Date.now());
        // TTL
        const expirationDate = addDays(Date.now(), offerDurationDays);
        this.expirationDate = getUnixTime(expirationDate);
        this.expirationDay = formatISO(expirationDate, { representation: 'date' });
    }
}
OfferDDBModel.VERSION = VERSION;
export const refreshExpirationDate = (offer, offerDurationDays) => {
    offer.publicationDate = formatISO(Date.now());
    // TTL
    const expirationDate = addDays(Date.now(), offerDurationDays);
    offer.expirationDate = getUnixTime(expirationDate);
    offer.expirationDay = formatISO(expirationDate, { representation: 'date' });
    offer.publicationDate;
    return offer;
};
export class OfferElasticSearchModel {
    constructor(source) {
        this.slug = source.slug;
        this.subject = source.subject;
        this.type = source.type;
        this.employmentType = source.employmentType;
        this.contractType = source.contractType;
        this.teachingType = source.teachingType;
        this.salary = source.salary;
        this.title = source.title;
        this.jobTitles = source.jobTitles;
        this.city = source.city;
        this.location = source.location ? LocationElasticSearchModel.fromILocation(source.location) : undefined;
        this.levels = source.levels;
        this.jobStartDate = source.jobStartDate;
        this.jobEndDate = '' === source.jobEndDate ? null : source.jobEndDate;
        this.description = source.description;
        this.candidateRequirements = source.candidateRequirements;
        this.benefits = source.benefits;
        this.establishmentId = source.establishmentId;
        if (source.establishment) {
            this.establishment = new EstablishmentElasticSearchModel(source.establishment);
        }
        this.publicationDate = source.publicationDate;
        this.expirationDate = source.expirationDate;
        this.expirationDay = source.expirationDay;
        this.status = source.status;
    }
}
export class OfferViewModel {
    constructor(source) {
        this.slug = source.slug;
        this.subject = source.subject;
        this.type = source.type;
        this.employmentType = source.employmentType;
        this.contractType = source.contractType;
        this.teachingType = source.teachingType;
        this.salary = source.salary;
        this.title = source.title;
        this.jobTitles = source.jobTitles;
        this.city = source.city;
        this.location = source.location ? LocationViewModel.fromILocation(source.location) : null;
        this.levels = source.levels;
        this.jobStartDate = source.jobStartDate;
        this.jobEndDate = source.jobEndDate;
        this.description = source.description;
        this.candidateRequirements = source.candidateRequirements;
        this.benefits = source.benefits;
        this.establishmentId = source.establishmentId;
        if (source.establishment) {
            this.establishment = new EstablishmentViewModel(source.establishment);
        }
        this.publicationDate = source.publicationDate;
        this.expirationDate = source.expirationDate;
        this.expirationDay = source.expirationDay;
        this.status = source.status;
    }
}
export default {
    OfferFormModel,
    OfferDDBModel
};
